import { Component, ElementRef, ViewChild } from "@angular/core";
import { ActiveStatus, ActiveStatusEnum, Feature, FeatureChoice } from "app/model/entities/entity-model";
import { DialogBaseComponent, DialogParams } from "app/shared/dialog-base.component";
import { DomainService } from "app/shared/domain.service";
import { ProductExposureFns, ProductExposureSummaryDTO } from "app/shared/product-exposure-api.service";
import { UtilFns } from "app/shared/util-fns";
import _ from "lodash";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";

export interface EditFeatureChoiceDialogParams extends DialogParams {
  // if featureChoice is null - add a new featureChoice for feature passed in.
  featureChoice?: FeatureChoice;
  feature?: Feature;
}

@Component({
  templateUrl: "./edit-feature-choice-dialog.component.html",
  standalone: false,
})
export class EditFeatureChoiceDialogComponent extends DialogBaseComponent<EditFeatureChoiceDialogParams> {
  @ViewChild("firstInput") firstInput: ElementRef<HTMLInputElement>;

  activeStatuses: ActiveStatus[];
  featureChoice: FeatureChoice;
  feature: Feature;
  availableFeatureChoices: FeatureChoice[];
  isAdding: boolean;
  /** From the Product Use panel; null until it answers. */
  productUse: ProductExposureSummaryDTO | null = null;

  constructor(public config: DynamicDialogConfig<any>, public dialogRef: DynamicDialogRef, public domainService: DomainService) {
    super(config, dialogRef, domainService);
    this.prepare();
  }

  /** Gatekeeper P3.6: the name and the reorder choice change what the used products mean. Status stays open for Phaseout. */
  get isMeaningLocked() {
    return !this.isAdding && ProductExposureFns.isMeaningLocked(this.productUse);
  }

  private async prepare() {
    this.activeStatuses = this.dbQueryService
      .getAllCached(ActiveStatus)
      .filter((x) => x.id === ActiveStatusEnum.Active || x.id === ActiveStatusEnum.Inactive);
    this.featureChoice = this.params.featureChoice;
    if (this.featureChoice == null) {
      this.isAdding = true;
      this.feature = this.params.feature;
      this.featureChoice = this.createNewFeatureChoiceAsUnchanged();
    } else {
      this.feature = this.featureChoice.feature;
    }
    this.availableFeatureChoices = await this.dbQueryService.getFeatureChoices(this.feature.id);
    this.availableFeatureChoices.filter((x) => x.id == this.featureChoice.id);
    this.availableFeatureChoices = _.sortBy(this.availableFeatureChoices, (x) => x.displayOrder);
    this.availableFeatureChoices.unshift({
      id: null as number,
      choiceValue: "- None -",
    } as FeatureChoice);
    this.config.header = this.isAdding ? "Add Feature Choice" : "Edit Feature Choice";
    this.isPageReady = true;

    // Focus the first input after the view renders
    await UtilFns.wait(1);
    this.firstInput?.nativeElement?.focus();
  }

  private createNewFeatureChoiceAsUnchanged() {
    const fc = this.dbSaveService.createEntity(FeatureChoice, {
      featureId: this.feature.id,
      activeStatusId: 1,
      displayOrder: 1,
    });
    fc.entityAspect.setUnchanged();
    fc.entityAspect.validateEntity();
    return fc;
  }

  async ok() {
    const ok = await this.save();
    if (!ok) return;
    this.dialogRef.close(true);
  }

  async save() {
    if (this.isAdding) {
      this.featureChoice.entityAspect.setAdded();
      this.featureChoice._origChoiceValue = this.featureChoice.choiceValue;
    }
    const isValid = await this.validate();
    if (!isValid) {
      return false;
    }
    try {
      const sr = await this.dbSaveService.saveChanges();
    } catch (e: any) {
      if (this.isAdding) {
        this.uow.rollback();
      }
      this.toastr.error(e?.message ?? "An error occurred while saving.");
      return false;
    }
    this.toastr.success("Feature Choice updated");
    return true;
  }

  async validate() {
    this.featureChoice._origChoiceValue = this.featureChoice._origChoiceValue ?? this.featureChoice.choiceValue;

    let matchingEntity = await this.dbQueryService.getMatchingEntity(FeatureChoice, this.featureChoice, ["choiceValue", "featureId"]);
    if (matchingEntity != null) {
      this.toastr.error("Cannot save — this Feature Choice value already exists.");
      return false;
    }
    const ea = this.featureChoice.entityAspect;
    if (!ea.validateEntity()) {
      const vErrors = ea.getValidationErrors();
      const errMsg = vErrors.map((ve) => ve.errorMessage).join("<br>");
      await this.dialogService.showOkMessage("Validation Error(s)", errMsg);
      return false;
    }
    return true;
  }

  async cancel() {
    // insures that we don't try to redisplay the page after the reject changes.
    this.isPageReady = false;
    if (this.isAdding) {
      this.featureChoice.entityAspect.setDetached();
    } else {
      this.featureChoice.entityAspect?.rejectChanges();
    }
    this.dialogRef.close(false);
  }
}
